import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, MutationCache, QueryCache } from "@tanstack/react-query";
import { captureException, isAuthError, showReportDialogIfSignedIn } from '@/lib/sentry';
import { ApiError } from '@/lib/api';
import { isChunkLoadError, reloadForStaleChunk } from '@/lib/chunkReload';
import { supabase } from '@/integrations/supabase/client';
import { BrowserRouter, Routes, Route, Navigate, createRoutesFromChildren } from "react-router-dom";
import { Analytics } from '@vercel/analytics/react';
import { createAnalyticsBeforeSend } from '@/lib/analytics';
import { PostHogIdentity } from '@/components/analytics/PostHogIdentity';
import { SidebarProvider } from "@/components/ui/sidebar";
import { Suspense, lazy } from "react";
import { AppLayout } from "@/components/AppLayout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RequireSuperAdmin } from "@/components/RequireSuperAdmin";
import { AuthProvider } from "@/hooks/useAuth";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { AppModeProvider } from "@/contexts/AppModeContext";
import { VoiceControlProvider } from "@/contexts/VoiceControlContext";
import { RouteTransition } from "@/components/RouteTransition";
import { FeatureGate } from "@/components/FeatureGate";
import { RequireOrgAdmin } from "@/components/RequireOrgAdmin";
import { RequireOrgScope } from "@/components/RequireOrgScope";
import { OrgIndexRedirect } from "@/components/OrgIndexRedirect";
import { SystemSettingsRedirect } from "@/components/SystemSettingsRedirect";
import { LEGACY_IMPACT_REDIRECTS } from "@/config/legacyRedirects";
import { LegacyRedirect } from "@/components/LegacyRedirect";
import { AccountingSetupGate } from "@/components/accounting/AccountingSetupGate";
import { Loader2 } from "lucide-react";
import * as Sentry from "@sentry/react";

// Wrap Routes with Sentry to track the actual route for error context
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Lazy loaded pages
const Auth = lazy(() => import("./pages/Auth"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Projects = lazy(() => import("./pages/Projects"));
const People = lazy(() => import("./pages/People"));
const Businesses = lazy(() => import("./pages/Businesses.tsx"));
const Places = lazy(() => import("./pages/Places"));
const Assets = lazy(() => import("./pages/Assets"));
const FundingOpportunities = lazy(() => import("./pages/resources/FundingOpportunities"));
const FundingOpportunitiesAdmin = lazy(() => import("./pages/settings/FundingOpportunitiesAdmin"));
const Files = lazy(() => import("./pages/Files"));
const FileDownload = lazy(() => import("./pages/FileDownload"));
const Reports = lazy(() => import("./pages/Reports"));
const Assessments = lazy(() => import("./pages/Assessments"));
const AssessmentConfig = lazy(() => import("./pages/AssessmentConfig"));
const Inbox = lazy(() => import("./pages/Inbox"));
const VerifyInboxEmail = lazy(() => import("./pages/VerifyInboxEmail"));
const BankCallback = lazy(() => import("./pages/BankCallback"));
const StripeCallback = lazy(() => import("./pages/StripeCallback"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const NotFound = lazy(() => import("./pages/NotFound"));
const NotFoundContent = lazy(() => import("./pages/NotFound").then(m => ({ default: m.NotFoundContent })));
const LegalDocumentPage = lazy(() => import("./pages/LegalDocumentPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ProjectDetails = lazy(() => import("./pages/ProjectDetails"));
const AssetDetails = lazy(() => import("./pages/AssetDetails"));
const PlaceDetails = lazy(() => import("./pages/PlaceDetails"));
const BusinessDetails = lazy(() => import("./pages/BusinessDetails.tsx"));
const PersonDetails = lazy(() => import("./pages/PersonDetails"));
const Profile = lazy(() => import("./pages/Profile"));
const InvoiceView = lazy(() => import("./pages/InvoiceView").then(m => ({ default: m.InvoiceView })));

// Project sub-pages
const ProjectOverviewPage = lazy(() => import("./pages/projects/ProjectOverviewPage"));
const ProjectAccountingPage = lazy(() => import("./pages/projects/ProjectAccountingPage"));
const ProjectTeamPage = lazy(() => import("./pages/projects/ProjectTeamPage"));
const ProjectAssetsPage = lazy(() => import("./pages/projects/ProjectAssetsPage"));
const ProjectSustainabilityPage = lazy(() => import("./pages/projects/ProjectSustainabilityPage"));
const ProjectPlacesPage = lazy(() => import("./pages/projects/ProjectPlacesPage"));
const ProjectChannelsPage = lazy(() => import("./pages/projects/ProjectChannelsPage"));
const ProjectEventsPage = lazy(() => import("./pages/projects/ProjectEventsPage"));
const ProjectEventPlannerPage = lazy(() => import("./pages/projects/ProjectEventPlannerPage"));

// Project accounting sub-pages
const ProjectAccountingFinancialPage = lazy(() => import("./pages/projects/ProjectAccountingFinancialPage"));
const ProjectAccountingExpensesPage = lazy(() => import("./pages/projects/ProjectAccountingExpensesPage"));
const ProjectAccountingRevenuePage = lazy(() => import("./pages/projects/ProjectAccountingRevenuePage"));
const ProjectAccountingPayablesPage = lazy(() => import("./pages/projects/ProjectAccountingPayablesPage"));
const ProjectAccountingReceivablesPage = lazy(() => import("./pages/projects/ProjectAccountingReceivablesPage"));

// People sub-pages
const PersonOverviewPage = lazy(() => import("./pages/people/PersonOverviewPage"));
const PersonAccountingPage = lazy(() => import("./pages/people/PersonAccountingPage"));
const PersonDisciplinesPage = lazy(() => import("./pages/people/PersonDisciplinesPage"));
const PersonConnectionsPage = lazy(() => import("./pages/people/PersonConnectionsPage"));
const PersonActivityPage = lazy(() => import("./pages/people/PersonActivityPage"));
const PersonDocumentsPage = lazy(() => import("./pages/people/PersonDocumentsPage"));
const PersonChannelsPage = lazy(() => import("./pages/people/PersonChannelsPage"));
const PersonInteractionsPage = lazy(() => import("./pages/people/PersonInteractionsPage"));
const Interactions = lazy(() => import("./pages/Interactions"));
const Surveys = lazy(() => import("./pages/Surveys"));
const SurveyDetails = lazy(() => import("./pages/SurveyDetails"));
const SurveyOverviewPage = lazy(() => import("./pages/surveys/SurveyOverviewPage"));
const SurveyResultsPage = lazy(() => import("./pages/surveys/SurveyResultsPage"));
const SurveyResponsesPage = lazy(() => import("./pages/surveys/SurveyResponsesPage"));
const SurveyChannelsPage = lazy(() => import("./pages/surveys/SurveyChannelsPage"));
const PublicSurvey = lazy(() => import("./pages/PublicSurvey"));
const PublicVolunteerSignup = lazy(() => import("./pages/PublicVolunteerSignup"));
const PublicVolunteerUnsubscribe = lazy(() => import("./pages/PublicVolunteerUnsubscribe"));
const ProjectInteractionsPage = lazy(() => import("./pages/projects/ProjectInteractionsPage"));
const ProjectSurveysPage = lazy(() => import("./pages/projects/ProjectSurveysPage"));
const ProjectVolunteersPage = lazy(() => import("./pages/projects/ProjectVolunteersPage"));
const InteractionTypes = lazy(() => import("./pages/settings/InteractionTypes"));
const InteractionRoles = lazy(() => import("./pages/settings/InteractionRoles"));

// Business sub-pages
const BusinessOverviewPage = lazy(() => import("./pages/businesses/BusinessOverviewPage"));
const BusinessAccountingPage = lazy(() => import("./pages/businesses/BusinessAccountingPage"));
const BusinessContactsPage = lazy(() => import("./pages/businesses/BusinessContactsPage"));
const BusinessLocationsPage = lazy(() => import("./pages/businesses/BusinessLocationsPage"));
const BusinessContractsPage = lazy(() => import("./pages/businesses/BusinessContractsPage"));
const BusinessProjectsPage = lazy(() => import("./pages/businesses/BusinessProjectsPage"));
const BusinessActivityPage = lazy(() => import("./pages/businesses/BusinessActivityPage"));
const BusinessChannelsPage = lazy(() => import("./pages/businesses/BusinessChannelsPage"));

// Location sub-pages
const PlaceOverviewPage = lazy(() => import("./pages/places/PlaceOverviewPage"));
const PlaceFacilitiesPage = lazy(() => import("./pages/places/PlaceFacilitiesPage"));
const PlaceSustainabilityPage = lazy(() => import("./pages/places/PlaceSustainabilityPage"));
const PlaceUtilitiesPage = lazy(() => import("./pages/places/PlaceUtilitiesPage"));
const PlaceUtilityAnalyticsPage = lazy(() => import("./pages/places/PlaceUtilityAnalyticsPage"));
const PlaceUtilityAccountPage = lazy(() => import("./pages/places/PlaceUtilityAccountPage"));
const PlaceProjectsPage = lazy(() => import("./pages/places/PlaceProjectsPage"));
const PlaceContactsPage = lazy(() => import("./pages/places/PlaceContactsPage"));
const PlaceChannelsPage = lazy(() => import("./pages/places/PlaceChannelsPage"));
const PlaceActivityPage = lazy(() => import("./pages/places/PlaceActivityPage"));

// Asset sub-pages
const AssetOverviewPage = lazy(() => import("./pages/assets/AssetOverviewPage"));
const AssetFinancialPage = lazy(() => import("./pages/assets/AssetFinancialPage"));
const AssetMaintenancePage = lazy(() => import("./pages/assets/AssetMaintenancePage"));
const AssetSustainabilityPage = lazy(() => import("./pages/assets/AssetSustainabilityPage"));
const AssetProjectHistoryPage = lazy(() => import("./pages/assets/AssetProjectHistoryPage"));
const AssetAttachmentsPage = lazy(() => import("./pages/assets/AssetAttachmentsPage"));
const AssetAuditPage = lazy(() => import("./pages/assets/AssetAuditPage"));
const AssetChannelsPage = lazy(() => import("./pages/assets/AssetChannelsPage"));

// Reports sub-pages
const AllReportsPage = lazy(() => import("./pages/reports/AllReportsPage"));
const RecentReportsPage = lazy(() => import("./pages/reports/RecentReportsPage"));


// Assessments sub-pages
const AllAssessmentsPage = lazy(() => import("./pages/assessments/AllAssessmentsPage"));
const InProgressAssessmentsPage = lazy(() => import("./pages/assessments/InProgressAssessmentsPage"));
const CompletedAssessmentsPage = lazy(() => import("./pages/assessments/CompletedAssessmentsPage"));
const NotStartedAssessmentsPage = lazy(() => import("./pages/assessments/NotStartedAssessmentsPage"));

// Level-Up pages
const LevelUpLayout = lazy(() => import("./pages/level-up/LevelUpLayout"));
const LevelUpDashboard = lazy(() => import("./pages/level-up/LevelUpDashboard"));
const LevelUpAssessmentDetail = lazy(() => import("./pages/level-up/LevelUpAssessmentDetail"));
const LevelUpAssessmentsList = lazy(() => import("./pages/level-up/LevelUpAssessmentsList"));
const LevelUpManagePage = lazy(() => import("./pages/level-up/manage/LevelUpManagePage"));
const LevelUpEditorPage = lazy(() => import("./pages/level-up/manage/LevelUpEditorPage"));

// Accounting section imports
const Accounting = lazy(() => import("./pages/Accounting"));
const AccountingConfiguration = lazy(() => import("./pages/AccountingConfiguration"));
const AccountDetails = lazy(() => import("./pages/AccountDetails"));
const AccountingOverview = lazy(() => import("./pages/AccountingOverview"));
const AccountingBudgets = lazy(() => import("./pages/AccountingBudgets"));
const AccountingBudgetDetails = lazy(() => import("./pages/AccountingBudgetDetails"));
const ChartOfAccountsTab = lazy(() => import("./components/accounting/ChartOfAccountsTab").then(m => ({ default: m.ChartOfAccountsTab })));
const TransactionsTab = lazy(() => import("./components/accounting/TransactionsTab").then(m => ({ default: m.TransactionsTab })));
const ExpensesTab = lazy(() => import("./components/accounting/ExpensesTab").then(m => ({ default: m.ExpensesTab })));
const APInvoicesTab = lazy(() => import("./components/accounting/APInvoicesTab").then(m => ({ default: m.APInvoicesTab })));
const ARInvoicesTab = lazy(() => import("./components/accounting/ARInvoicesTab").then(m => ({ default: m.ARInvoicesTab })));
const FundsTab = lazy(() => import("./components/accounting/FundsTab").then(m => ({ default: m.FundsTab })));
const FundDetails = lazy(() => import("./pages/FundDetails"));
const FundOverviewPage = lazy(() => import("./pages/funds/FundOverviewPage"));
const FundInstallmentsPage = lazy(() => import("./pages/funds/FundInstallmentsPage"));
const FundTransactionsPage = lazy(() => import("./pages/funds/FundTransactionsPage"));
const FundBudgetPage = lazy(() => import("./pages/funds/FundBudgetPage"));
const RecurringDocumentsTab = lazy(() => import("./components/accounting/recurring/RecurringDocumentsTab").then(m => ({ default: m.RecurringDocumentsTab })));
const CurrencyTransferTab = lazy(() => import("./components/accounting/CurrencyTransferTab").then(m => ({ default: m.CurrencyTransferTab })));
const BankAccounts = lazy(() => import("./pages/BankAccounts"));
const SalesChannels = lazy(() => import("./pages/SalesChannels"));
const CreditCards = lazy(() => import("./pages/CreditCards"));
const OtherAccounts = lazy(() => import("./pages/OtherAccounts"));
const AccountingTax = lazy(() => import("./pages/AccountingTax"));

// Timesheets
const Timesheets = lazy(() => import("./pages/Timesheets"));
const TimesheetDetails = lazy(() => import("./pages/TimesheetDetails"));

// Memos
const Memos = lazy(() => import("./pages/Memos"));
const MemoDetail = lazy(() => import("./pages/MemoDetail"));

// Mobile Apps
const AppsLayout = lazy(() => import("./components/AppsLayout").then(m => ({ default: m.AppsLayout })));
const MobileTimesheet = lazy(() => import("./pages/apps/MobileTimesheet"));
const MobileBookkeeper = lazy(() => import("./pages/apps/MobileBookkeeper"));
// The volunteer phone app (organiser screens; docs/design/volunteer-mobile).
const VolunteersProjectPicker = lazy(() => import("./pages/apps/volunteers/VolunteersProjectPicker"));
const VolunteersApp = lazy(() => import("./pages/apps/volunteers/VolunteersApp"));
const VolunteersLanding = lazy(() => import("./pages/apps/volunteers/VolunteersApp").then(m => ({ default: m.VolunteersLanding })));
const VolunteersOverviewScreen = lazy(() => import("./pages/apps/volunteers/OverviewScreen"));
const VolunteersShiftsScreen = lazy(() => import("./pages/apps/volunteers/ShiftsScreen"));
const VolunteersPeopleScreen = lazy(() => import("./pages/apps/volunteers/PeopleScreen"));
const VolunteersRoleScreen = lazy(() => import("./pages/apps/volunteers/RoleScreen"));
const VolunteersShiftScreen = lazy(() => import("./pages/apps/volunteers/ShiftScreen"));
const VolunteersCrewScreen = lazy(() => import("./pages/apps/volunteers/CrewScreen"));
// Dev-only preview of the touch-surface components (never in a production build).
const VolunteerComponentPreview = import.meta.env.DEV
  ? lazy(() => import("./pages/apps/volunteers/ComponentPreview"))
  : null;

// Sustainability
const Sustainability = lazy(() => import("./pages/Sustainability"));

// Settings section imports
const Settings = lazy(() => import("./pages/Settings"));
const OrganizationSettings = lazy(() => import("./pages/OrganizationSettings"));
const TeamSettings = lazy(() => import("./pages/TeamSettings"));
const EmissionFactorTables = lazy(() => import("./pages/settings/EmissionFactorTables"));
const EmissionFactorTableDetail = lazy(() => import("./pages/settings/EmissionFactorTableDetail"));
const OrgEmissionFactors = lazy(() => import("./pages/settings/OrgEmissionFactors"));
const AiConnectors = lazy(() => import("./pages/settings/AiConnectors"));
const EmissionBindings = lazy(() => import("./pages/settings/EmissionBindings"));
const CarbonFormulas = lazy(() => import("./pages/settings/CarbonFormulas"));
const BedesDictionary = lazy(() => import("./pages/settings/bedes/BedesDictionary"));
const BedesTermDetails = lazy(() => import("./pages/settings/bedes/BedesTermDetails"));
const BedesCompositeDetails = lazy(() => import("./pages/settings/bedes/BedesCompositeDetails"));
const BedesMappingListDetails = lazy(() => import("./pages/settings/bedes/BedesMappingListDetails"));
const AssetTypes = lazy(() => import("./pages/settings/AssetTypes"));
const ProjectTypes = lazy(() => import("./pages/settings/ProjectTypes"));
const BusinessTypes = lazy(() => import("./pages/settings/BusinessTypes"));
const ProcurementTypes = lazy(() => import("./pages/settings/ProcurementTypes"));
const RecycledContentTypes = lazy(() => import("./pages/settings/RecycledContentTypes"));
const EndOfLifePlans = lazy(() => import("./pages/settings/EndOfLifePlans"));
const WasteTreatmentMethods = lazy(() => import("./pages/settings/WasteTreatmentMethods"));

// System Settings section imports (super admin only)
const SystemSettings = lazy(() => import("./pages/SystemSettings"));
const SuperAdminUsers = lazy(() => import("./pages/settings/SuperAdminUsers"));
const SystemAssessments = lazy(() => import("./pages/settings/SystemAssessments"));
const FeatureFlags = lazy(() => import("./pages/settings/FeatureFlags"));
const Bookkeeper = lazy(() => import("./pages/settings/Bookkeeper"));
const TaxRateTemplates = lazy(() => import("./pages/settings/TaxRateTemplates"));
const ChartOfAccountsTemplatesAdmin = lazy(() => import("./pages/settings/ChartOfAccountsTemplatesAdmin"));
const DeletionAudit = lazy(() => import("./pages/settings/DeletionAudit"));
const TaxSettings = lazy(() => import("./pages/settings/TaxSettings"));
const Privacy = lazy(() => import("./pages/settings/Privacy"));
const Usage = lazy(() => import("./pages/settings/Usage"));
const AuditLog = lazy(() => import("./pages/settings/AuditLog"));
const PlatformUsage = lazy(() => import("./pages/settings/PlatformUsage"));
const SubscriptionSettings = lazy(() => import("./pages/settings/Subscription"));
const SubscriptionsAdmin = lazy(() => import("./pages/settings/SubscriptionsAdmin"));
const PlaceDetailFields = lazy(() => import("./pages/settings/PlaceDetailFields"));
const ClassificationFieldMapping = lazy(() => import("./pages/settings/ClassificationFieldMapping"));
const ConnectionTypes = lazy(() => import("./pages/settings/ConnectionTypes"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));

// Test component
const AddressLookupTest = lazy(() => import("./components/AddressLookupTest").then(m => ({ default: m.AddressLookupTest })));

// Help pages
const HelpPage = lazy(() => import("./pages/help/HelpPage"));
const HelpLandingPage = lazy(() => import("./pages/help/HelpLandingPage"));
const HelpCategoryPage = lazy(() => import("./pages/help/HelpCategoryPage"));
const HelpArticlePage = lazy(() => import("./pages/help/HelpArticlePage"));

// Org selection
const OrgSelect = lazy(() => import("./pages/OrgSelect"));

// Org layout and root redirect
import { OrgLayout } from "./components/OrgLayout";
import { RootRedirect } from "./components/RootRedirect";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (isAuthError(error)) return false;
        // Backend 403s (feature flag disabled, role gates) are deterministic
        // authorization outcomes — not auth errors (isAuthError excludes
        // them), but retrying is equally pointless.
        if (error instanceof ApiError && error.status === 403) return false;
        return failureCount < 3;
      },
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Mutations can declare expected, user-benign failure codes via
      // meta.expectedErrorCodes (e.g. the bank feed's "row already processed
      // in another tab" race). Those still reject — the owning hook handles
      // them with a warning toast — but they are not error events, so don't
      // inflate Sentry with them. The code rides on the EnvelopeError thrown
      // by unwrapEnvelope (@/lib/api).
      const expectedCodes = mutation.meta?.expectedErrorCodes;
      const code = (error as { envelope?: { code?: string } }).envelope?.code;
      if (Array.isArray(expectedCodes) && code && expectedCodes.includes(code)) {
        return;
      }
      captureException(error, {
        source: 'react-query-mutation',
        mutationKey: mutation.options.mutationKey,
      });
    },
  }),
  queryCache: new QueryCache({
    onError: (error, query) => {
      captureException(error, {
        source: 'react-query-query',
        queryKey: query.queryKey,
      });
    },
  }),
});

// A dead session's refetch burst (window-focus refetches racing the redirect
// to /login) can only produce RLS-empty data and auth errors — cancel anything
// in flight the moment the session ends. Explicit sign-out hard-redirects the
// page, so this mainly covers a failed token refresh in a stale tab.
supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT') {
    void queryClient.cancelQueries();
  }
});

const AppErrorFallback = ({ error }: { error: unknown }) => {
  // Stale chunk after a deploy: reload once (lib/chunkReload.ts)
  if (isChunkLoadError(error) && reloadForStaleChunk()) {
    return null;
  }

  return (
    <div className="flex items-center justify-center h-screen w-full">
      <div className="text-center space-y-4 p-8">
        <h1 className="text-2xl font-semibold text-destructive">Something went wrong</h1>
        <p className="text-muted-foreground">The error has been reported to our team.</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          Reload Page
        </button>
      </div>
    </div>
  );
};

// The route tree, shared by <SentryRoutes> and the analytics URL redaction
// (which matches each page view against the same patterns).
const appRoutes = (
  <>
    {/* Auth routes - no org context needed */}
    <Route path="/auth" element={<RouteTransition><Auth /></RouteTransition>} />
    <Route path="/auth/callback" element={<RouteTransition><AuthCallback /></RouteTransition>} />
    <Route path="/login" element={<RouteTransition><Auth /></RouteTransition>} />
    {/* Legacy legal links — redirect to releve.life (lib/legalDocuments.ts); public, no auth, no org, no AppLayout */}
    <Route path="/legal/:kind" element={<RouteTransition><LegalDocumentPage /></RouteTransition>} />
    {/* Public survey answer page — anonymous, no session, no org, no AppLayout, outside every
        role / flag gate (docs/SURVEYS.md). The token is the credential; the API answers a uniform 404. */}
    <Route path="/s/:token" element={<RouteTransition><PublicSurvey /></RouteTransition>} />
    {/* Crew-email unsubscribe (ruling 39): the static path, before (and ranked above) /v/:token. */}
    <Route path="/v/unsubscribe" element={<RouteTransition><PublicVolunteerUnsubscribe /></RouteTransition>} />
    <Route path="/v/:token" element={<RouteTransition><PublicVolunteerSignup /></RouteTransition>} />

    {/* Org selection - for users with no org or selecting org */}
    <Route path="/org-select" element={<ProtectedRoute><RouteTransition><OrgSelect /></RouteTransition></ProtectedRoute>} />

    {/* Root redirect - sends to org-scoped dashboard */}
    <Route path="/" element={<ProtectedRoute><RootRedirect /></ProtectedRoute>} />

    {/* Accept invitation - needs to work without org context */}
    <Route path="/accept-invitation" element={<ProtectedRoute><AcceptInvitation /></ProtectedRoute>} />

    {/* Profile - user-level, not org-scoped */}
    <Route path="/profile" element={<ProtectedRoute><AppLayout navigation="account"><RouteTransition><Profile /></RouteTransition></AppLayout></ProtectedRoute>} />

    {/* Inbox email verification - reached from email link, no org context needed */}
    <Route path="/verify-inbox-email" element={<ProtectedRoute><AppLayout navigation="account"><RouteTransition><VerifyInboxEmail /></RouteTransition></AppLayout></ProtectedRoute>} />

    {/* Bank feed sync (Enable Banking) return URL. Deliberately org-less:
      Enable Banking redirects to ONE registered path per environment, so
      the org is recovered from the signed `state` payload and the page
      navigates on to /org/:orgId/... itself. */}
    <Route path="/bank-callback" element={<ProtectedRoute><RouteTransition><BankCallback /></RouteTransition></ProtectedRoute>} />
    {/* Org-less by force, like /bank-callback: the Relevé Stripe App's
        install link returns to ONE redirect URI per environment (the
        manifest's allowed_redirect_uris); the page reads the org out of the
        signed state (pages/StripeCallback.tsx). */}
    <Route path="/stripe-callback" element={<ProtectedRoute><RouteTransition><StripeCallback /></RouteTransition></ProtectedRoute>} />

    {/* OAuth 2.1 consent (Supabase Auth's `authorization_url_path`) — an AI app
      asking to reach Ré's tools over MCP. Org-less: grants are per user. */}
    <Route path="/oauth/consent" element={<ProtectedRoute><RouteTransition><OAuthConsent /></RouteTransition></ProtectedRoute>} />

    {/* System Settings (super admin only) — platform-wide, so outside the org routes:
        its own off-org sidebar, no organization needed (docs/NAVIGATION.md "Off-org navigation"). */}
    <Route path="/system-settings" element={<ProtectedRoute><RequireSuperAdmin><AppLayout navigation="system"><RouteTransition><SystemSettings /></RouteTransition></AppLayout></RequireSuperAdmin></ProtectedRoute>}>
      <Route index element={<Navigate to="users" replace />} />
      <Route path="users" element={<RouteTransition><SuperAdminUsers /></RouteTransition>} />
      <Route path="assessments" element={<RouteTransition><SystemAssessments /></RouteTransition>} />
      <Route path="asset-types" element={<RouteTransition><AssetTypes scopeMode="system" /></RouteTransition>} />
      <Route path="project-types" element={<RouteTransition><ProjectTypes scopeMode="system" /></RouteTransition>} />
      <Route path="business-types" element={<RouteTransition><BusinessTypes scopeMode="system" /></RouteTransition>} />
      {/* No FeatureGate: the global half is platform-wide, and a gate would read an org's flag. */}
      <Route path="interaction-types" element={<RouteTransition><InteractionTypes scopeMode="system" /></RouteTransition>} />
      <Route path="interaction-roles" element={<RouteTransition><InteractionRoles scopeMode="system" /></RouteTransition>} />
      <Route path="procurement-types" element={<RouteTransition><ProcurementTypes scopeMode="system" /></RouteTransition>} />
      <Route path="recycled-content-types" element={<RouteTransition><RecycledContentTypes scopeMode="system" /></RouteTransition>} />
      <Route path="end-of-life-plans" element={<RouteTransition><EndOfLifePlans scopeMode="system" /></RouteTransition>} />
      <Route path="waste-treatment-methods" element={<RouteTransition><WasteTreatmentMethods scopeMode="system" /></RouteTransition>} />
      <Route path="funding-opportunities" element={<RouteTransition><FundingOpportunitiesAdmin /></RouteTransition>} />
      <Route path="emission-factors" element={<RouteTransition><EmissionFactorTables /></RouteTransition>} />
      <Route path="emission-factors/:tableCode" element={<RouteTransition><EmissionFactorTableDetail scopeMode="system" /></RouteTransition>} />
      <Route path="bedes" element={<RouteTransition><BedesDictionary /></RouteTransition>} />
      <Route path="bedes/terms/:termId" element={<RouteTransition><BedesTermDetails /></RouteTransition>} />
      <Route path="bedes/composites/:compositeId" element={<RouteTransition><BedesCompositeDetails /></RouteTransition>} />
      <Route path="bedes/mappings/:mappingListId" element={<RouteTransition><BedesMappingListDetails /></RouteTransition>} />
      <Route path="feature-flags" element={<RouteTransition><FeatureFlags /></RouteTransition>} />
      <Route path="bookkeeper" element={<RouteTransition><Bookkeeper /></RouteTransition>} />
      {/* Old URL for the models page, pre-dating the tabbed Bookkeeper page */}
      <Route path="bookkeeper-models" element={<Navigate to="../bookkeeper?tab=models" replace />} />
      {/* No FeatureGate: it would read the selected org's flag (none here) and bounce a super admin. */}
      <Route path="usage" element={<RouteTransition><PlatformUsage /></RouteTransition>} />
      <Route path="subscriptions" element={<RouteTransition><SubscriptionsAdmin /></RouteTransition>} />
      <Route path="tax-rate-templates" element={<RouteTransition><TaxRateTemplates /></RouteTransition>} />
      <Route path="coa-templates" element={<RouteTransition><ChartOfAccountsTemplatesAdmin /></RouteTransition>} />
      <Route path="deletion-audit" element={<RouteTransition><DeletionAudit /></RouteTransition>} />
    </Route>

    {/* Org-scoped routes */}
    <Route path="/org/:orgId" element={<ProtectedRoute><OrgLayout /></ProtectedRoute>}>
    {/* Default org page: accounting overview, or the dashboard for a role without the `accounting` scope */}
    <Route index element={<OrgIndexRedirect />} />
    <Route path="dashboard" element={<AppLayout><RouteTransition><Dashboard /></RouteTransition></AppLayout>} />
    <Route path="projects" element={<AppLayout><RouteTransition><Projects /></RouteTransition></AppLayout>} />
    <Route path="projects/:code" element={<AppLayout><RouteTransition><ProjectDetails /></RouteTransition></AppLayout>}>
      <Route index element={<Navigate to="overview" replace />} />
      <Route path="overview" element={<RouteTransition><ProjectOverviewPage /></RouteTransition>} />
      <Route path="accounting" element={<RequireOrgScope scope="accounting"><RouteTransition><ProjectAccountingPage /></RouteTransition></RequireOrgScope>}>
        <Route index element={<Navigate to="financial" replace />} />
        <Route path="financial" element={<RouteTransition><ProjectAccountingFinancialPage /></RouteTransition>} />
        <Route path="expenses" element={<RouteTransition><ProjectAccountingExpensesPage /></RouteTransition>} />
        <Route path="revenue" element={<RouteTransition><ProjectAccountingRevenuePage /></RouteTransition>} />
        <Route path="payables" element={<RouteTransition><ProjectAccountingPayablesPage /></RouteTransition>} />
        <Route path="receivables" element={<RouteTransition><ProjectAccountingReceivablesPage /></RouteTransition>} />
      </Route>
      <Route path="team" element={<RouteTransition><ProjectTeamPage /></RouteTransition>} />
      <Route path="volunteers" element={<FeatureGate featureKey="volunteer_self_signup"><RouteTransition><ProjectVolunteersPage /></RouteTransition></FeatureGate>} />
      <Route path="interactions" element={<FeatureGate featureKey="interactions"><RouteTransition><ProjectInteractionsPage /></RouteTransition></FeatureGate>} />
      <Route path="surveys" element={<FeatureGate featureKey="surveys"><RequireOrgScope scope="surveys"><RouteTransition><ProjectSurveysPage /></RouteTransition></RequireOrgScope></FeatureGate>} />
      <Route path="assets" element={<RouteTransition><ProjectAssetsPage /></RouteTransition>} />
      <Route path="sustainability" element={<RouteTransition><ProjectSustainabilityPage /></RouteTransition>} />
      <Route path="places" element={<RouteTransition><ProjectPlacesPage /></RouteTransition>} />
      {/* M5.7: the legacy Locations tab became Places — keep old bookmarks working. */}
      <Route path="locations" element={<Navigate to="../places" replace />} />
      <Route path="events" element={<FeatureGate featureKey="project_events"><RouteTransition><ProjectEventsPage /></RouteTransition></FeatureGate>} />
      <Route path="channels" element={<RouteTransition><ProjectChannelsPage /></RouteTransition>} />
    </Route>
    {/* The event planner is a SIBLING of ProjectDetails, not nested in its
        <Outlet/>: it owns its header and a full-height, internally
        scrolling layout (AppLayout fullHeight). The secondary nav still
        reads the project code from the URL, so the Events item stays active. */}
    <Route path="projects/:code/events/:eventCode" element={<AppLayout fullHeight><FeatureGate featureKey="project_events"><RouteTransition><ProjectEventPlannerPage /></RouteTransition></FeatureGate></AppLayout>} />
    {/* Resources section - consolidated People, Businesses, Locations, Assets, Vault */}
    <Route path="resources">
      {/* Assets */}
      <Route path="assets" element={<AppLayout><RouteTransition><Assets /></RouteTransition></AppLayout>} />
      <Route path="assets/:code" element={<AppLayout><RouteTransition><AssetDetails /></RouteTransition></AppLayout>}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<RouteTransition><AssetOverviewPage /></RouteTransition>} />
        <Route path="projects" element={<RouteTransition><AssetProjectHistoryPage /></RouteTransition>} />
        <Route path="financial" element={<RequireOrgScope scope="accounting"><RouteTransition><AssetFinancialPage /></RouteTransition></RequireOrgScope>} />
        <Route path="maintenance" element={<RouteTransition><AssetMaintenancePage /></RouteTransition>} />
        <Route path="sustainability" element={<RouteTransition><AssetSustainabilityPage /></RouteTransition>} />
        <Route path="attachments" element={<RouteTransition><AssetAttachmentsPage /></RouteTransition>} />
        <Route path="audit" element={<RouteTransition><AssetAuditPage /></RouteTransition>} />
        {/* Retired: the activity tab's browser-written trail is now the trigger-written Audit tab. */}
        <Route path="activity" element={<Navigate to="../audit" replace />} />
        <Route path="channels" element={<RouteTransition><AssetChannelsPage /></RouteTransition>} />
      </Route>
      {/* Businesses */}
      <Route path="businesses" element={<AppLayout><RouteTransition><Businesses /></RouteTransition></AppLayout>} />
      <Route path="businesses/:code" element={<AppLayout><RouteTransition><BusinessDetails /></RouteTransition></AppLayout>}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<RouteTransition><BusinessOverviewPage /></RouteTransition>} />
        <Route path="accounting" element={<RequireOrgScope scope="accounting"><RouteTransition><BusinessAccountingPage /></RouteTransition></RequireOrgScope>} />
        <Route path="contacts" element={<RouteTransition><BusinessContactsPage /></RouteTransition>} />
        <Route path="locations" element={<RouteTransition><BusinessLocationsPage /></RouteTransition>} />
        <Route path="contracts" element={<RouteTransition><BusinessContractsPage /></RouteTransition>} />
        <Route path="projects" element={<RequireOrgScope scope="accounting"><RouteTransition><BusinessProjectsPage /></RouteTransition></RequireOrgScope>} />
        <Route path="activity" element={<RouteTransition><BusinessActivityPage /></RouteTransition>} />
        <Route path="channels" element={<RouteTransition><BusinessChannelsPage /></RouteTransition>} />
      </Route>
      {/* Places (evolved from Locations — the list page stays ungated so the
          Addresses grid keeps working when the 'places' flag is off) */}
      <Route path="places" element={<AppLayout><RouteTransition><Places /></RouteTransition></AppLayout>} />
      <Route path="places/:code" element={<AppLayout><FeatureGate featureKey="places"><RouteTransition><PlaceDetails /></RouteTransition></FeatureGate></AppLayout>}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<RouteTransition><PlaceOverviewPage /></RouteTransition>} />
        <Route path="facilities" element={<RouteTransition><PlaceFacilitiesPage /></RouteTransition>} />
        <Route path="sustainability" element={<RouteTransition><PlaceSustainabilityPage /></RouteTransition>} />
        <Route path="utilities" element={<FeatureGate featureKey="places_utilities"><RouteTransition><PlaceUtilitiesPage /></RouteTransition></FeatureGate>} />
        {/* Static segment — React Router ranks it above utilities/:accountId regardless of order. */}
        <Route path="utilities/analytics" element={<FeatureGate featureKey="places_utilities"><RouteTransition><PlaceUtilityAnalyticsPage /></RouteTransition></FeatureGate>} />
        <Route path="utilities/:accountId" element={<FeatureGate featureKey="places_utilities"><RouteTransition><PlaceUtilityAccountPage /></RouteTransition></FeatureGate>} />
        {/* The sub-spaces card moved onto Overview (R7 — uses vs
            sub-spaces adjacency); keep old bookmarks working. */}
        <Route path="sub-spaces" element={<Navigate to="../overview" replace />} />
        {/* Map folded into Overview; keep old bookmarks working. */}
        <Route path="map" element={<Navigate to="../overview" replace />} />
        <Route path="projects" element={<RouteTransition><PlaceProjectsPage /></RouteTransition>} />
        <Route path="contacts" element={<RouteTransition><PlaceContactsPage /></RouteTransition>} />
        <Route path="channels" element={<RouteTransition><PlaceChannelsPage /></RouteTransition>} />
        <Route path="activity" element={<RouteTransition><PlaceActivityPage /></RouteTransition>} />
      </Route>
      {/* Backward-compat: old locations URLs land on the places list (old
          LOC- detail codes have no PLC counterpart, so deep links degrade
          to the list — accepted in the Places plan). */}
      <Route path="locations" element={<Navigate to="../places" replace />} />
      <Route path="locations/:code/*" element={<Navigate to="../../places" replace />} />
      {/* Funding Opportunities */}
      <Route path="funding-opportunities" element={<AppLayout><RouteTransition><FundingOpportunities /></RouteTransition></AppLayout>} />
      {/* People */}
      <Route path="people" element={<AppLayout><RouteTransition><People /></RouteTransition></AppLayout>} />
      {/* Static segment before people/:code — React Router ranks it above the param anyway. */}
      <Route path="people/interactions" element={<AppLayout><FeatureGate featureKey="interactions"><RouteTransition><Interactions /></RouteTransition></FeatureGate></AppLayout>} />
      <Route path="people/surveys" element={<AppLayout><FeatureGate featureKey="surveys"><RequireOrgScope scope="surveys"><RouteTransition><Surveys /></RouteTransition></RequireOrgScope></FeatureGate></AppLayout>} />
      <Route path="people/surveys/:code" element={<AppLayout><FeatureGate featureKey="surveys"><RequireOrgScope scope="surveys"><RouteTransition><SurveyDetails /></RouteTransition></RequireOrgScope></FeatureGate></AppLayout>}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<RouteTransition><SurveyOverviewPage /></RouteTransition>} />
        <Route path="results" element={<RouteTransition><SurveyResultsPage /></RouteTransition>} />
        <Route path="responses" element={<RouteTransition><SurveyResponsesPage /></RouteTransition>} />
        <Route path="channels" element={<RouteTransition><SurveyChannelsPage /></RouteTransition>} />
      </Route>
      <Route path="people/:code" element={<AppLayout><RouteTransition><PersonDetails /></RouteTransition></AppLayout>}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<RouteTransition><PersonOverviewPage /></RouteTransition>} />
        <Route path="interactions" element={<FeatureGate featureKey="interactions"><RouteTransition><PersonInteractionsPage /></RouteTransition></FeatureGate>} />
        <Route path="accounting" element={<RequireOrgScope scope="accounting"><RouteTransition><PersonAccountingPage /></RouteTransition></RequireOrgScope>} />
        <Route path="disciplines" element={<RouteTransition><PersonDisciplinesPage /></RouteTransition>} />
        <Route path="connections" element={<RouteTransition><PersonConnectionsPage /></RouteTransition>} />
        <Route path="activity" element={<RouteTransition><PersonActivityPage /></RouteTransition>} />
        <Route path="documents" element={<RouteTransition><PersonDocumentsPage /></RouteTransition>} />
        <Route path="channels" element={<RouteTransition><PersonChannelsPage /></RouteTransition>} />
      </Route>
      {/* Vault */}
      <Route path="vault" element={<AppLayout><RouteTransition><Files /></RouteTransition></AppLayout>} />
      {/* The five legacy Vault sub-tabs collapsed into the one index grid.
          Never delete a route, only its page — old bookmarks must land
          somewhere sensible. These are FLAT SIBLINGS under `resources`, so
          ".." resolves to `resources` and "../vault" to `resources/vault`. */}
      <Route path="vault/all" element={<Navigate to="../vault" replace />} />
      <Route path="vault/documents" element={<Navigate to="../vault" replace />} />
      <Route path="vault/reports" element={<Navigate to="../vault" replace />} />
      <Route path="vault/images" element={<Navigate to="../vault" replace />} />
      <Route path="vault/recent" element={<Navigate to="../vault" replace />} />
      <Route path="vault/download/:fileId/:fileName" element={<RouteTransition><FileDownload /></RouteTransition>} />
    </Route>
    {/* Redirects for backward compatibility */}
    <Route path="people/*" element={<Navigate to="../resources/people" replace />} />
    <Route path="businesses/*" element={<Navigate to="../resources/businesses" replace />} />
    <Route path="locations/*" element={<Navigate to="../resources/places" replace />} />
    <Route path="assets/*" element={<Navigate to="../resources/assets" replace />} />
    {/* The scope guard sits OUTSIDE AccountingSetupGate, so a project manager is
        redirected before the tax-setup dialog can open. */}
    <Route path="accounting" element={<AppLayout><RequireOrgScope scope="accounting"><AccountingSetupGate><RouteTransition><Accounting /></RouteTransition></AccountingSetupGate></RequireOrgScope></AppLayout>}>
      <Route index element={<Navigate to="overview" replace />} />
      <Route path="overview" element={<RouteTransition><AccountingOverview /></RouteTransition>} />
      <Route path="accounts" element={<RouteTransition><ChartOfAccountsTab organizationId={undefined} /></RouteTransition>} />
      <Route path="transactions" element={<RouteTransition><TransactionsTab organizationId={undefined} /></RouteTransition>} />
      <Route path="expenses" element={<RouteTransition><ExpensesTab /></RouteTransition>} />
      <Route path="payables" element={<RouteTransition><APInvoicesTab /></RouteTransition>} />
      <Route path="receivables" element={<RouteTransition><ARInvoicesTab /></RouteTransition>} />
      {/* Sales Channels — ONE route; the selected channel and the
          active tab ride in query params (?channel=&tab=). */}
      <Route path="sales-channels" element={<FeatureGate featureKey="sales_channels"><RouteTransition><SalesChannels /></RouteTransition></FeatureGate>} />
      <Route path="transfers" element={<RouteTransition><CurrencyTransferTab /></RouteTransition>} />
      <Route path="recurring" element={<FeatureGate featureKey="recurring_documents"><RouteTransition><RecurringDocumentsTab /></RouteTransition></FeatureGate>} />
      <Route path="bank-accounts" element={<RouteTransition><BankAccounts /></RouteTransition>} />
      <Route path="credit-cards" element={<RouteTransition><CreditCards /></RouteTransition>} />
      <Route path="other-accounts" element={<RouteTransition><OtherAccounts /></RouteTransition>} />
      <Route path="funds" element={<RouteTransition><FundsTab organizationId={undefined} /></RouteTransition>} />
      {/* Fund detail (fund management P1) — bookmarkable nested tabs. */}
      <Route path="funds/:code" element={<FeatureGate featureKey="fund_management"><RouteTransition><FundDetails /></RouteTransition></FeatureGate>}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<RouteTransition><FundOverviewPage /></RouteTransition>} />
        <Route path="installments" element={<RouteTransition><FundInstallmentsPage /></RouteTransition>} />
        <Route path="transactions" element={<RouteTransition><FundTransactionsPage /></RouteTransition>} />
        <Route path="budget" element={<FeatureGate featureKey="accounting_budgets"><RouteTransition><FundBudgetPage /></RouteTransition></FeatureGate>} />
      </Route>
      <Route path="budgets" element={<FeatureGate featureKey="accounting_budgets"><RouteTransition><AccountingBudgets /></RouteTransition></FeatureGate>} />
      <Route path="budgets/:id" element={<FeatureGate featureKey="accounting_budgets"><RouteTransition><AccountingBudgetDetails /></RouteTransition></FeatureGate>} />
      <Route path="accounts/:id" element={<RouteTransition><AccountDetails /></RouteTransition>} />
      <Route path="receivables/:invoiceId" element={<RouteTransition><InvoiceView type="receivable" /></RouteTransition>} />
      <Route path="payables/:invoiceId" element={<RouteTransition><InvoiceView type="payable" /></RouteTransition>} />
      <Route path="tax" element={<RouteTransition><AccountingTax /></RouteTransition>} />
    </Route>
    {/* Impact lives outside Accounting (and its setup gate) so every role
        reaches it; old /accounting/impact and /sustainability links redirect. */}
    <Route path="impact" element={<AppLayout><RouteTransition><Sustainability /></RouteTransition></AppLayout>} />
    {LEGACY_IMPACT_REDIRECTS.map((redirect) => (
      <Route key={redirect.path} path={redirect.path} element={<LegacyRedirect from={redirect.from} to={redirect.to} />} />
    ))}
    {/* Redirect old grants route to new location */}
    <Route path="grants" element={<Navigate to="../resources/funding-opportunities" replace />} />
    <Route path="timesheets" element={<AppLayout><RouteTransition><Timesheets /></RouteTransition></AppLayout>} />
    <Route path="timesheets/:id" element={<AppLayout><RouteTransition><TimesheetDetails /></RouteTransition></AppLayout>} />
    <Route path="memos" element={<AppLayout><FeatureGate featureKey="memos"><RouteTransition><Memos /></RouteTransition></FeatureGate></AppLayout>} />
    <Route path="memos/:id" element={<AppLayout><FeatureGate featureKey="memos"><RouteTransition><MemoDetail /></RouteTransition></FeatureGate></AppLayout>} />
    <Route path="vault/*" element={<Navigate to="../resources/vault" replace />} />
    <Route path="inbox" element={<AppLayout><RouteTransition><Inbox /></RouteTransition></AppLayout>} />
    <Route path="reports" element={<AppLayout><RouteTransition><Reports /></RouteTransition></AppLayout>}>
      <Route index element={<Navigate to="all" replace />} />
      <Route path="all" element={<RouteTransition><AllReportsPage /></RouteTransition>} />
      <Route path="recent" element={<RouteTransition><RecentReportsPage /></RouteTransition>} />
      {/* Legacy per-category report pages, consolidated into /reports/all */}
      <Route path="financial" element={<Navigate to="../all" replace />} />
      <Route path="sustainability" element={<Navigate to="../all" replace />} />
      <Route path="analytics" element={<Navigate to="../all" replace />} />
      <Route path="entities" element={<Navigate to="../all" replace />} />
    </Route>
    <Route path="assessments" element={<AppLayout><FeatureGate featureKey="assessments"><RouteTransition><Assessments /></RouteTransition></FeatureGate></AppLayout>}>
      <Route index element={<Navigate to="all" replace />} />
      <Route path="all" element={<RouteTransition><AllAssessmentsPage /></RouteTransition>} />
      <Route path="in-progress" element={<RouteTransition><InProgressAssessmentsPage /></RouteTransition>} />
      <Route path="completed" element={<RouteTransition><CompletedAssessmentsPage /></RouteTransition>} />
      <Route path="not-started" element={<RouteTransition><NotStartedAssessmentsPage /></RouteTransition>} />
    </Route>
    <Route path="assessment-config" element={<AppLayout><FeatureGate featureKey="assessments"><RouteTransition><AssessmentConfig /></RouteTransition></FeatureGate></AppLayout>} />

    {/* Level-Up Section */}
    <Route path="level-up" element={<AppLayout><FeatureGate featureKey="level_up"><RouteTransition><LevelUpLayout /></RouteTransition></FeatureGate></AppLayout>}>
      <Route index element={<RouteTransition><LevelUpDashboard /></RouteTransition>} />
      <Route path="assessments" element={<RouteTransition><LevelUpAssessmentsList /></RouteTransition>} />
      <Route path="assessments/:assessmentId" element={<RouteTransition><LevelUpAssessmentDetail /></RouteTransition>} />
    </Route>
    {/* Level Up builder (Phase 2): top-level siblings of the legacy routes, so
        neither changes the other; the editor is a full-height three-pane page. */}
    <Route path="level-up/manage" element={<AppLayout><FeatureGate featureKey="level_up_authoring"><RequireOrgScope scope="level_up"><RouteTransition><LevelUpManagePage /></RouteTransition></RequireOrgScope></FeatureGate></AppLayout>} />
    <Route path="level-up/manage/:assessmentKey/versions/:version" element={<AppLayout fullHeight><FeatureGate featureKey="level_up_authoring"><RequireOrgScope scope="level_up"><RouteTransition><LevelUpEditorPage /></RouteTransition></RequireOrgScope></FeatureGate></AppLayout>} />

    {/* Help / Knowledge Base */}
    <Route path="help" element={<AppLayout><RouteTransition><HelpPage /></RouteTransition></AppLayout>}>
      <Route index element={<RouteTransition><HelpLandingPage /></RouteTransition>} />
      <Route path=":category" element={<RouteTransition><HelpCategoryPage /></RouteTransition>} />
      <Route path=":category/:slug" element={<RouteTransition><HelpArticlePage /></RouteTransition>} />
    </Route>

    <Route path="settings" element={<AppLayout><RouteTransition><Settings /></RouteTransition></AppLayout>}>
      <Route index element={<RouteTransition><OrganizationSettings /></RouteTransition>} />
      <Route path="organization" element={<RouteTransition><OrganizationSettings /></RouteTransition>} />
      <Route path="accounting" element={<RequireOrgScope scope="accounting"><RouteTransition><AccountingConfiguration /></RouteTransition></RequireOrgScope>} />
      <Route path="accounts" element={<RequireOrgScope scope="accounting"><RouteTransition><ChartOfAccountsTab organizationId={undefined} /></RouteTransition></RequireOrgScope>} />
      <Route path="accounts/:id" element={<RequireOrgScope scope="accounting"><RouteTransition><AccountDetails /></RouteTransition></RequireOrgScope>} />
      <Route path="team" element={<RouteTransition><TeamSettings /></RouteTransition>} />
      <Route path="ai-connectors" element={<FeatureGate featureKey="mcp_access"><RouteTransition><AiConnectors /></RouteTransition></FeatureGate>} />
      <Route path="tax" element={<RequireOrgScope scope="accounting"><RouteTransition><TaxSettings /></RouteTransition></RequireOrgScope>} />
      <Route path="emission-factors" element={<RouteTransition><OrgEmissionFactors /></RouteTransition>} />
      <Route path="emission-factors/:tableCode" element={<RouteTransition><EmissionFactorTableDetail /></RouteTransition>} />
      <Route path="emission-bindings" element={<FeatureGate featureKey="places_emissions"><RouteTransition><EmissionBindings /></RouteTransition></FeatureGate>} />
      {/* The selected org's own formulas; no guard or flag — RLS lets every member write them. */}
      <Route path="carbon-formulas" element={<RouteTransition><CarbonFormulas /></RouteTransition>} />
      <Route path="privacy" element={<RouteTransition><Privacy /></RouteTransition>} />
      <Route path="audit-log" element={<RequireOrgScope scope="accounting_admin" superAdminBypass><RouteTransition><AuditLog /></RouteTransition></RequireOrgScope>} />
      <Route path="usage" element={<FeatureGate featureKey="usage_tracking"><RequireOrgAdmin><RouteTransition><Usage /></RouteTransition></RequireOrgAdmin></FeatureGate>} />
      <Route path="subscription" element={<FeatureGate featureKey="subscriptions"><RequireOrgAdmin><RouteTransition><SubscriptionSettings /></RouteTransition></RequireOrgAdmin></FeatureGate>} />
      <Route path="place-fields" element={<FeatureGate featureKey="places"><RouteTransition><PlaceDetailFields /></RouteTransition></FeatureGate>} />
      <Route path="place-field-mapping" element={<FeatureGate featureKey="places"><RouteTransition><ClassificationFieldMapping /></RouteTransition></FeatureGate>} />
      <Route path="connection-types" element={<RouteTransition><ConnectionTypes /></RouteTransition>} />
      {/* Dual-scope lists (org half; the global half is under /system-settings) */}
      <Route path="business-types" element={<RouteTransition><BusinessTypes scopeMode="org" /></RouteTransition>} />
      <Route path="project-types" element={<RouteTransition><ProjectTypes scopeMode="org" /></RouteTransition>} />
      <Route path="asset-types" element={<RouteTransition><AssetTypes scopeMode="org" /></RouteTransition>} />
      <Route path="procurement-types" element={<RouteTransition><ProcurementTypes scopeMode="org" /></RouteTransition>} />
      <Route path="recycled-content-types" element={<RouteTransition><RecycledContentTypes scopeMode="org" /></RouteTransition>} />
      <Route path="end-of-life-plans" element={<RouteTransition><EndOfLifePlans scopeMode="org" /></RouteTransition>} />
      <Route path="waste-treatment-methods" element={<RouteTransition><WasteTreatmentMethods scopeMode="org" /></RouteTransition>} />
      <Route path="interaction-types" element={<FeatureGate featureKey="interactions"><RouteTransition><InteractionTypes scopeMode="org" /></RouteTransition></FeatureGate>} />
      <Route path="interaction-roles" element={<FeatureGate featureKey="interactions"><RouteTransition><InteractionRoles scopeMode="org" /></RouteTransition></FeatureGate>} />
    </Route>

    {/* System Settings moved to the org-less /system-settings (above); old links redirect,
        keeping the rest of the path, ?tab= and the hash. */}
    <Route path="system-settings/*" element={<SystemSettingsRedirect />} />

    <Route path="test-azure-maps" element={<AppLayout><RouteTransition><AddressLookupTest /></RouteTransition></AppLayout>} />

    {/* Mobile Apps - minimal layout, no sidebar */}
    <Route path="apps" element={<RouteTransition><AppsLayout /></RouteTransition>}>
      <Route path="timesheets" element={<RouteTransition><MobileTimesheet /></RouteTransition>} />
      <Route path="bookkeeper" element={<RouteTransition><MobileBookkeeper /></RouteTransition>} />
      <Route path="volunteers" element={<FeatureGate featureKey="volunteer_self_signup"><RouteTransition><VolunteersProjectPicker /></RouteTransition></FeatureGate>} />
      <Route path="volunteers/:projectCode" element={<FeatureGate featureKey="volunteer_self_signup"><RouteTransition><VolunteersApp /></RouteTransition></FeatureGate>}>
        <Route index element={<RouteTransition><VolunteersLanding /></RouteTransition>} />
        <Route path="overview" element={<RouteTransition><VolunteersOverviewScreen /></RouteTransition>} />
        <Route path="shifts" element={<RouteTransition><VolunteersShiftsScreen /></RouteTransition>} />
        <Route path="shifts/:shiftId" element={<RouteTransition><VolunteersShiftScreen /></RouteTransition>} />
        <Route path="people" element={<RouteTransition><VolunteersPeopleScreen /></RouteTransition>} />
        <Route path="roles/:roleId" element={<RouteTransition><VolunteersRoleScreen /></RouteTransition>} />
        <Route path="crew" element={<RouteTransition><VolunteersCrewScreen /></RouteTransition>} />
      </Route>
      {VolunteerComponentPreview && (
        <Route path="volunteers/__preview" element={<RouteTransition><VolunteerComponentPreview /></RouteTransition>} />
      )}
    </Route>

    {/* Catch-all for unmatched org routes - renders within AppLayout */}
    <Route path="*" element={<AppLayout><RouteTransition><NotFoundContent /></RouteTransition></AppLayout>} />
    </Route>

    {/* 404 route */}
    <Route path="/404" element={<RouteTransition><NotFound /></RouteTransition>} />
    {/* Catch-all */}
    <Route path="*" element={<RouteTransition><NotFound /></RouteTransition>} />
  </>
);

// One route matcher for both analytics sinks: Vercel's beforeSend and
// PostHog's before_send reduce every URL to its route pattern.
const appRouteObjects = createRoutesFromChildren(appRoutes);
const analyticsBeforeSend = createAnalyticsBeforeSend(appRouteObjects);

const App = () => (
  <Sentry.ErrorBoundary
    fallback={AppErrorFallback}
    onError={showReportDialogIfSignedIn}
  >
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Sonner />
        <BrowserRouter>
          {/* Vercel Analytics — pageview tracking. Renders nothing; no-op in dev (mode='auto').
              URLs are reduced to route patterns first — see lib/analytics.ts. */}
          <Analytics beforeSend={analyticsBeforeSend} />
          <AuthProvider>
            <OrganizationProvider>
              <AppModeProvider>
              {/* PostHog — starts after sign-in, unless the user opted out. */}
              <PostHogIdentity routes={appRouteObjects} />
              <VoiceControlProvider>
              <SidebarProvider>
            <Suspense fallback={
              <div className="flex items-center justify-center h-screen w-full bg-gradient-to-br from-background to-muted/20">
                <div className="text-center space-y-6 p-8">
                  <div className="flex justify-center mb-8">
                    <img
                      src="/releve_black.svg"
                      alt="Relevé Logo"
                      className="h-16 w-auto"
                    />
                  </div>
                  <div className="flex justify-center">
                    <Loader2 className="h-16 w-16 animate-spin text-primary" />
                  </div>
                </div>
              </div>
            }>
              <SentryRoutes>{appRoutes}</SentryRoutes>
            </Suspense>
            </SidebarProvider>
              </VoiceControlProvider>
              </AppModeProvider>
          </OrganizationProvider>
        </AuthProvider>
      </BrowserRouter>
        </TooltipProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
